import { regular } from '@fortawesome/fontawesome-svg-core/import.macro';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Breadcrumb from '../../components/breadcrumb/Breadcrumb.component';
import MetaComp from '../../components/meta-comp/MetaComp.component';
import ProgressBar from '../../components/progress-bar/ProgressBar.component';
import SingleEventContainer from '../../containers/task-dashboard/SingleEvent.container';
import SingleEventHeaderContainer from '../../containers/task-dashboard/SingleEventHeader.container';
import TaskTimelineContainer from '../../containers/task-timeline/TaskTimeline.container';
import { saveClientData } from '../../redux/actions/clientData.actions';
import { appError, appErrorClear } from '../../redux/actions/error.action';
import { openSnackBar } from '../../redux/actions/snackbar.actions';
import { getApiUrl } from '../../utils/apiUrls';
import instance from '../../utils/axios';
import { segregateTasks } from '../../utils/helperFn';
import ErrorController from '../error/ErrorController.page';
import { logout } from '../../redux/actions/user.actions';
import ConsentCrfContainer from '../../containers/task-dashboard/crf/ConsentCrf.container';
import { apiErrorHandler } from '../../utils/errorHandler';

const Eventpage = () => {
    
    const chkError = useSelector((state) => state.error);
    const params = useParams();
    const [searchParams] = useSearchParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const token = React.useMemo(() => localStorage.getItem('token'), []);

    const refId = searchParams.get('refId');
    const [evId, setEvId] = React.useState(null);
    const [taskData, setTaskData] = React.useState(null);
    const [joinModalData, setJoinModalData] = React.useState({
        customDialogBoxToggle: false,
        taskStartDate: null,
        interviewLink: null
      });

    const [crfData, setCrfData] = React.useState({
      data: null,
      isLoading: true,
      showCrf: false,
      isSaveBtnLoading: false,
      hasGetConsentDetailsRun: false 
    });

    const joinModalDataHandler = (val) =>{
        setJoinModalData(val);
    }

    const breadcrumbData = React.useMemo(() => ([
        {
            iconClass: regular("house"),
            link: '/'
        },
        {
            label: t("dashboard"),
            link: '/'
        },
        {
            label: ''
        }
    ]), [t]);

    useEffect(() => {
      getCandidateConsentDetails(params.eventSlug);
    }, [params]);

    React.useEffect(() => {
        if(crfData.hasGetConsentDetailsRun && !crfData.showCrf) fetchTasks(params.eventSlug);
        setEvId(params.eventSlug);
    }, [params, crfData]);

    const getCandidateConsentDetails = async (eventId) => {
      try {
        const res = await instance.get(`${getApiUrl('getCandidateConsentDetails')}?eventId=${eventId}&consentType=POST_LOGIN_CONSENT`, {
          headers: {
            "Authorization" : `Bearer ${token}`,
          }
        });

        if(res.data) {
          setCrfData({
            data: res.data,
            isLoading: false,
            showCrf: res.data.showConsentPage,
            hasGetConsentDetailsRun: true
          });
        }

      } catch (error) {
        if(error.response.status === 401) dispatch(logout())
        else appError('tasksPage', error, dispatch);
      }
    }

    const handleSaveConsentDetails = async (dataToSend) => {
      try {
        setCrfData((prev) => ({
          ...prev,
          isSaveBtnLoading: true
        }));

        const res = await instance.post(`${getApiUrl('saveCandidateConsentDetails')}?eventId=${evId}&consentType=POST_LOGIN_CONSENT`, dataToSend, {
          headers: {
            "Authorization" : `Bearer ${token}`,
          }
        });

        if(res.data) {
          dispatch(openSnackBar({
            msg: t(res.data.message || "consent_saved_successfully"),
            type: "success"
          }))

          setCrfData((prev) => ({
            ...prev,
            showCrf: false,
            isSaveBtnLoading: false
          }));
        }
        
      } catch (error) {
        console.log("error", error);
        const errObj = apiErrorHandler(error);
        dispatch(openSnackBar({
            msg: t(errObj.statusText),
            type: 'error'
        }))
        setCrfData((prev) => ({
          ...prev,
          isSaveBtnLoading: false
        }));
      }
    }

    const surveyDataHandler = (data) => {
        let temp = []
        if(!!data.selfLink){
          temp = [...temp,{...data,approverLink:null,nominationLink:null,name: `${t("provide_feedback")}- ${data.name}`,taskName: `${t("provide_feedback")}- ${data.taskName}`}]
        }
        if (!!data.nominationLink) {
          temp = [
            ...temp,
            {
              ...data,
              approverLink: null,
              selfLink: null,
              taskStatus: !data.nominationCompleted
                ? "YET_TO_START"
                : "COMPLETED",
              taskTab: !data.nominationCompleted ? "YET_TO_START" : "COMPLETED",
              name: `${t("add_provider")}- ${data.name}`,
              taskName: `${t("add_provider")}- ${data.taskName}`,
            },
          ];
        }
        if (!!data.approverLink) {
          temp = [
            ...temp,
            {
              ...data,
              selfLink: null,
              nominationLink: null,
              taskStatus: !data.approvalCompleted
                ? "YET_TO_START"
                : "COMPLETED",
              taskTab: !data.approvalCompleted ? "YET_TO_START" : "COMPLETED",
              name: `${t("approve_providers")}- ${data.name}`,// NORMAL TASK
              taskName: `${t("approve_providers")}- ${data.taskName}`, // GROUP AND SEQUENCE TASK
            },
          ];
        }
        return temp;
    }

    const fetchTasks = async (eventId) => {
        try {
            // const token = getToken();

            const res = await instance.get(`${getApiUrl('getCandidateEventDetails')}?eventId=${eventId}`, {
                headers: {
                    "Authorization" : `Bearer ${token}`,
                }
            });
            if(res.data.status === 'invalid' && res.data.statusCode === 'E106') {
                dispatch(openSnackBar({
                    msg: t(res.data.message),
                    type: 'error'
                }));

                navigate('/')
            }

            if(!!res.data) {
                dispatch(saveClientData({ logo: res.data?.logoUrl }))

                breadcrumbData[2].label = res.data.eventName; //Assign event name
                let candidateAllTaskList = [] 
                res.data.candidateGroupEventTaskDetailDTOList.forEach((el) => {
                  if (el.taskType === "FEEDBACK_SURVEY") {
                    const temp = surveyDataHandler(el);
                    candidateAllTaskList = [...candidateAllTaskList, ...temp];
                    return;
                  }
                  if (!el?.taskType && el.candidateTaskList.length > 0) {
                    // TO MANAGE GROUP AND SEQUENCE FOR SURVEY
                    let candidateTaskList = [];
                    el.candidateTaskList.forEach((innerEl) => {
                      if (innerEl.taskType === "FEEDBACK_SURVEY") {
                        const temp = surveyDataHandler(innerEl);
                        candidateTaskList = [...candidateTaskList, ...temp];
                        return;
                    }
                    candidateTaskList.push(innerEl);
                  });
                   el.candidateTaskList = candidateTaskList;
                  }
                  candidateAllTaskList.push(el);
                });
                const {candidateTaskList, candidateCompletedTaskList, count} = segregateTasks(candidateAllTaskList);                
                setTaskData({...res.data, candidateAllTaskList, candidateTaskList, candidateCompletedTaskList, count })
            }  

            if(!!res.data && chkError.location === 'tasksPage') appErrorClear(dispatch);

        } catch (error) {
            console.log("error", error);
            if(error.response.status === 401) dispatch(logout())
            else appError('tasksPage', error, dispatch);
        }   
    }

    const handleError = () => chkError.location === 'task-timeline' || chkError.location === 'tasksPage'

    return(
        handleError() ? <ErrorController data={chkError} /> : <div className={`cm-eventpage-container ${crfData.showCrf ? "" : "cm-cand-page-container"}`}>
            {
              crfData.showCrf ? <ConsentCrfContainer isSaveBtnLoading={crfData.isSaveBtnLoading} crfData={crfData.data} t={t} handleSaveConsentDetails={handleSaveConsentDetails} />
              : (
                taskData !== null ?
                <>
                    <Breadcrumb data={breadcrumbData} />
                      <SingleEventHeaderContainer taskData={taskData} refId={refId} />
                      <SingleEventContainer taskData={taskData} joinModalData={joinModalData} joinModalDataHandler={joinModalDataHandler}/>
                      <TaskTimelineContainer taskData={taskData} eventId={evId} joinModalData={joinModalData} joinModalDataHandler={joinModalDataHandler}/>
                </>
                : 
                <div className='py-5 px-4'>
                    <ProgressBar />
                </div>
              )
            }
            <MetaComp title={`${!!taskData ? taskData.eventName + " - " : ''} Marsh`} />
            <Outlet />
        </div>
    )

}

export default Eventpage;